/**
 * SentenceEditModal Component
 * Modal for editing sentence content in creator mode:
 * - Text and translation
 * - Grammar points
 * - Cultural notes
 * - Speaking strategy (chunks and tips)
 * - Key vocabulary
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AnnotatedSentence,
  GrammarPoint,
  CulturalNote,
  SpeakingStrategy,
  SpeakingChunk,
} from '../../types/creator-workflow';
import './SentenceEditModal.css';

interface SentenceEditModalProps {
  sentence: AnnotatedSentence;
  sentenceIndex: number;
  onSave: (index: number, updates: Partial<AnnotatedSentence>) => Promise<void>;
  onClose: () => void;
  isLoading?: boolean;
}

type EditSection = 'text' | 'grammar' | 'cultural' | 'speaking' | 'vocabulary';

const SentenceEditModal: React.FC<SentenceEditModalProps> = ({
  sentence,
  sentenceIndex,
  onSave,
  onClose,
  isLoading = false,
}) => {
  const { t } = useTranslation(['creator', 'common']);
  const [activeSection, setActiveSection] = useState<EditSection>('text');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const firstInputRef = useRef<HTMLTextAreaElement>(null);

  // Handle escape key to close modal
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && !saving) {
      onClose();
    }
  }, [onClose, saving]);

  // Focus trap and escape key handling
  useEffect(() => {
    // Store previously focused element
    const previouslyFocused = document.activeElement as HTMLElement;

    // Focus first input
    firstInputRef.current?.focus();

    // Add escape key listener
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Restore focus when modal closes
      previouslyFocused?.focus();
    };
  }, [handleKeyDown]);

  // Form state
  const [text, setText] = useState(sentence.text || '');
  const [translationZhTw, setTranslationZhTw] = useState(sentence.translation || '');
  const [grammarPoints, setGrammarPoints] = useState<GrammarPoint[]>(
    sentence.grammar_points || []
  );
  const [culturalNotes, setCulturalNotes] = useState<CulturalNote | null>(
    sentence.cultural_notes || null
  );
  const [speakingStrategy, setSpeakingStrategy] = useState<SpeakingStrategy | null>(
    sentence.speaking_strategy || null
  );
  const [keyVocabulary, setKeyVocabulary] = useState<string[]>(
    sentence.key_vocabulary_in_sentence || []
  );

  // Reset form when sentence changes
  useEffect(() => {
    setText(sentence.text || '');
    setTranslationZhTw(sentence.translation || '');
    setGrammarPoints(sentence.grammar_points || []);
    setCulturalNotes(sentence.cultural_notes || null);
    setSpeakingStrategy(sentence.speaking_strategy || null);
    setKeyVocabulary(sentence.key_vocabulary_in_sentence || []);
  }, [sentence]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const updates: Partial<AnnotatedSentence> = {
        text,
        translation: translationZhTw,
        grammar_points: grammarPoints.length > 0 ? grammarPoints : undefined,
        cultural_notes: culturalNotes || undefined,
        speaking_strategy: speakingStrategy || undefined,
        key_vocabulary_in_sentence: keyVocabulary.length > 0 ? keyVocabulary : undefined,
      };

      await onSave(sentenceIndex, updates);
      onClose();
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(t('creator:workflow.sentenceEditModal.failedToSave'));
      }
    } finally {
      setSaving(false);
    }
  };

  // Grammar point handlers
  const addGrammarPoint = () => {
    setGrammarPoints([
      ...grammarPoints,
      {
        explanation_en: '',
        explanation_native: '',
        example: '',
        example_native: '',
      },
    ]);
  };

  const updateGrammarPoint = (index: number, field: keyof GrammarPoint, value: string) => {
    const updated = [...grammarPoints];
    updated[index] = { ...updated[index], [field]: value };
    setGrammarPoints(updated);
  };

  const removeGrammarPoint = (index: number) => {
    setGrammarPoints(grammarPoints.filter((_, i) => i !== index));
  };

  // Speaking chunk handlers
  const addSpeakingChunk = () => {
    if (!speakingStrategy) {
      setSpeakingStrategy({
        chunks: [{ text: '', translation: '', focus: '' }],
        tips_native: '',
      });
    } else {
      setSpeakingStrategy({
        ...speakingStrategy,
        chunks: [
          ...speakingStrategy.chunks,
          { text: '', translation: '', focus: '' },
        ],
      });
    }
  };

  const updateSpeakingChunk = (
    index: number,
    field: keyof SpeakingChunk,
    value: string
  ) => {
    if (!speakingStrategy) return;
    const updated = [...speakingStrategy.chunks];
    updated[index] = { ...updated[index], [field]: value };
    setSpeakingStrategy({ ...speakingStrategy, chunks: updated });
  };

  const removeSpeakingChunk = (index: number) => {
    if (!speakingStrategy) return;
    const updated = speakingStrategy.chunks.filter((_, i) => i !== index);
    if (updated.length === 0) {
      setSpeakingStrategy(null);
    } else {
      setSpeakingStrategy({ ...speakingStrategy, chunks: updated });
    }
  };

  // Key vocabulary handlers
  const addKeyVocab = () => {
    setKeyVocabulary([...keyVocabulary, '']);
  };

  const updateKeyVocab = (index: number, value: string) => {
    const updated = [...keyVocabulary];
    updated[index] = value;
    setKeyVocabulary(updated);
  };

  const removeKeyVocab = (index: number) => {
    setKeyVocabulary(keyVocabulary.filter((_, i) => i !== index));
  };

  const sections: { key: EditSection; label: string; icon: string }[] = [
    { key: 'text', label: t('creator:workflow.sentenceEditModal.tabText'), icon: '📝' },
    { key: 'grammar', label: t('creator:workflow.sentenceEditModal.tabGrammar'), icon: '📖' },
    { key: 'cultural', label: t('creator:workflow.sentenceEditModal.tabCultural'), icon: '🌏' },
    { key: 'speaking', label: t('creator:workflow.sentenceEditModal.tabSpeaking'), icon: '🗣️' },
    { key: 'vocabulary', label: t('creator:workflow.sentenceEditModal.tabVocabulary'), icon: '⭐' },
  ];

  return (
    <div
      className="sentence-edit-modal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="sentence-modal-title"
    >
      <div
        ref={modalRef}
        className="sentence-edit-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 id="sentence-modal-title">{t('creator:workflow.sentenceEditModal.title', { index: sentenceIndex + 1 })}</h2>
          <button
            type="button"
            className="btn-close"
            onClick={onClose}
            aria-label={t('creator:workflow.sentenceEditModal.closeModal')}
          >
            ✕
          </button>
        </div>

        {error && <div className="modal-error">{error}</div>}

        <div className="modal-tabs">
          {sections.map((section) => (
            <button
              key={section.key}
              className={`tab-btn ${activeSection === section.key ? 'active' : ''}`}
              onClick={() => setActiveSection(section.key)}
            >
              <span className="tab-icon">{section.icon}</span>
              <span className="tab-label">{section.label}</span>
            </button>
          ))}
        </div>

        <div className="modal-content">
          {/* Text Section */}
          {activeSection === 'text' && (
            <div className="edit-section">
              <div className="form-group">
                <label htmlFor="sentence-text">{t('creator:workflow.sentenceEditModal.englishText')}</label>
                <textarea
                  ref={firstInputRef}
                  id="sentence-text"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={t('creator:workflow.sentenceEditModal.englishPlaceholder')}
                  rows={3}
                />
              </div>
              <div className="form-group">
                <label>{t('creator:workflow.sentenceEditModal.chineseTranslation')}</label>
                <textarea
                  value={translationZhTw}
                  onChange={(e) => setTranslationZhTw(e.target.value)}
                  placeholder={t('creator:workflow.sentenceEditModal.chinesePlaceholder')}
                  rows={3}
                />
              </div>
            </div>
          )}

          {/* Grammar Points Section */}
          {activeSection === 'grammar' && (
            <div className="edit-section">
              <div className="section-header">
                <h3>{t('creator:workflow.sentenceEditModal.grammarPoints')}</h3>
                <button className="btn-add" onClick={addGrammarPoint}>
                  {t('creator:workflow.sentenceEditModal.addGrammarPoint')}
                </button>
              </div>

              {grammarPoints.length === 0 && (
                <p className="empty-hint">{t('creator:workflow.sentenceEditModal.noGrammarPoints')}</p>
              )}

              {grammarPoints.map((point, idx) => (
                <div key={idx} className="edit-item-card">
                  <div className="item-header">
                    <span className="item-number">#{idx + 1}</span>
                    <button
                      className="btn-remove"
                      onClick={() => removeGrammarPoint(idx)}
                    >
                      ✕
                    </button>
                  </div>
                  <div className="form-group">
                    <label>{t('creator:workflow.sentenceEditModal.explanationEn')}</label>
                    <textarea
                      value={point.explanation_en}
                      onChange={(e) =>
                        updateGrammarPoint(idx, 'explanation_en', e.target.value)
                      }
                      placeholder={t('creator:workflow.sentenceEditModal.explanationEnPlaceholder')}
                      rows={2}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('creator:workflow.sentenceEditModal.explanationZh')}</label>
                    <textarea
                      value={point.explanation_native}
                      onChange={(e) =>
                        updateGrammarPoint(idx, 'explanation_native', e.target.value)
                      }
                      placeholder={t('creator:workflow.sentenceEditModal.explanationZhPlaceholder')}
                      rows={2}
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('creator:workflow.sentenceEditModal.example')}</label>
                      <input
                        type="text"
                        value={point.example}
                        onChange={(e) =>
                          updateGrammarPoint(idx, 'example', e.target.value)
                        }
                        placeholder={t('creator:workflow.sentenceEditModal.examplePlaceholder')}
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('creator:workflow.sentenceEditModal.exampleZh')}</label>
                      <input
                        type="text"
                        value={point.example_native}
                        onChange={(e) =>
                          updateGrammarPoint(idx, 'example_native', e.target.value)
                        }
                        placeholder={t('creator:workflow.sentenceEditModal.exampleZhPlaceholder')}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Cultural Notes Section */}
          {activeSection === 'cultural' && (
            <div className="edit-section">
              <div className="section-header">
                <h3>{t('creator:workflow.sentenceEditModal.culturalNotes')}</h3>
                {!culturalNotes && (
                  <button
                    className="btn-add"
                    onClick={() =>
                      setCulturalNotes({ note_en: '', note_native: '' })
                    }
                  >
                    {t('creator:workflow.sentenceEditModal.addCulturalNote')}
                  </button>
                )}
              </div>

              {!culturalNotes ? (
                <p className="empty-hint">
                  {t('creator:workflow.sentenceEditModal.noCulturalNotes')}
                </p>
              ) : (
                <div className="edit-item-card">
                  <div className="item-header">
                    <span>{t('creator:workflow.sentenceEditModal.culturalNote')}</span>
                    <button
                      className="btn-remove"
                      onClick={() => setCulturalNotes(null)}
                    >
                      ✕
                    </button>
                  </div>
                  <div className="form-group">
                    <label>{t('creator:workflow.sentenceEditModal.noteEn')}</label>
                    <textarea
                      value={culturalNotes.note_en}
                      onChange={(e) =>
                        setCulturalNotes({
                          ...culturalNotes,
                          note_en: e.target.value,
                        })
                      }
                      placeholder={t('creator:workflow.sentenceEditModal.noteEnPlaceholder')}
                      rows={3}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('creator:workflow.sentenceEditModal.noteZh')}</label>
                    <textarea
                      value={culturalNotes.note_native}
                      onChange={(e) =>
                        setCulturalNotes({
                          ...culturalNotes,
                          note_native: e.target.value,
                        })
                      }
                      placeholder={t('creator:workflow.sentenceEditModal.noteZhPlaceholder')}
                      rows={3}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Speaking Strategy Section */}
          {activeSection === 'speaking' && (
            <div className="edit-section">
              <div className="section-header">
                <h3>{t('creator:workflow.sentenceEditModal.speakingStrategy')}</h3>
                <button className="btn-add" onClick={addSpeakingChunk}>
                  {t('creator:workflow.sentenceEditModal.addChunk')}
                </button>
              </div>

              {/* Tips */}
              <div className="form-group">
                <label>{t('creator:workflow.sentenceEditModal.tipsZh')}</label>
                <textarea
                  value={speakingStrategy?.tips_native || ''}
                  onChange={(e) =>
                    setSpeakingStrategy({
                      chunks: speakingStrategy?.chunks || [],
                      tips_native: e.target.value,
                    })
                  }
                  placeholder={t('creator:workflow.sentenceEditModal.tipsPlaceholder')}
                  rows={2}
                />
              </div>

              {/* Chunks */}
              <h4>{t('creator:workflow.sentenceEditModal.chunks')}</h4>
              {(!speakingStrategy || speakingStrategy.chunks.length === 0) && (
                <p className="empty-hint">
                  {t('creator:workflow.sentenceEditModal.noChunks')}
                </p>
              )}

              {speakingStrategy?.chunks.map((chunk, idx) => (
                <div key={idx} className="edit-item-card">
                  <div className="item-header">
                    <span className="item-number">{t('creator:workflow.sentenceEditModal.chunkNumber', { index: idx + 1 })}</span>
                    <button
                      className="btn-remove"
                      onClick={() => removeSpeakingChunk(idx)}
                    >
                      ✕
                    </button>
                  </div>
                  <div className="form-group">
                    <label>{t('creator:workflow.sentenceEditModal.chunkText')}</label>
                    <input
                      type="text"
                      value={chunk.text}
                      onChange={(e) =>
                        updateSpeakingChunk(idx, 'text', e.target.value)
                      }
                      placeholder={t('creator:workflow.sentenceEditModal.chunkTextPlaceholder')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('creator:workflow.sentenceEditModal.chunkTranslation')}</label>
                    <input
                      type="text"
                      value={chunk.translation}
                      onChange={(e) =>
                        updateSpeakingChunk(idx, 'translation', e.target.value)
                      }
                      placeholder={t('creator:workflow.sentenceEditModal.chunkTranslationPlaceholder')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('creator:workflow.sentenceEditModal.chunkFocus')}</label>
                    <input
                      type="text"
                      value={chunk.focus}
                      onChange={(e) =>
                        updateSpeakingChunk(idx, 'focus', e.target.value)
                      }
                      placeholder={t('creator:workflow.sentenceEditModal.chunkFocusPlaceholder')}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Key Vocabulary Section */}
          {activeSection === 'vocabulary' && (
            <div className="edit-section">
              <div className="section-header">
                <h3>{t('creator:workflow.sentenceEditModal.keyVocabulary')}</h3>
                <button className="btn-add" onClick={addKeyVocab}>
                  {t('creator:workflow.sentenceEditModal.addWord')}
                </button>
              </div>

              {keyVocabulary.length === 0 && (
                <p className="empty-hint">
                  {t('creator:workflow.sentenceEditModal.noKeyVocabulary')}
                </p>
              )}

              <div className="vocab-tags-edit">
                {keyVocabulary.map((word, idx) => (
                  <div key={idx} className="vocab-tag-input">
                    <input
                      type="text"
                      value={word}
                      onChange={(e) => updateKeyVocab(idx, e.target.value)}
                      placeholder={t('creator:workflow.sentenceEditModal.wordPlaceholder')}
                    />
                    <button
                      className="btn-remove-inline"
                      onClick={() => removeKeyVocab(idx)}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose} disabled={saving}>
            {t('common:cancel')}
          </button>
          <button
            className="btn-save"
            onClick={handleSave}
            disabled={saving || isLoading}
          >
            {saving ? t('common:saving') : t('creator:workflow.sentenceEditModal.saveChanges')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SentenceEditModal;
